import { Link } from '@tanstack/react-router';
import { Search, Menu } from 'lucide-react';
import { UserMenu } from './UserMenu';
import { setSidebarOpen, usePanels } from '@/store/uiPanels';

/**
 * Top bar — neo-brutalism, minimalist.
 *
 * Pre-redesign top bar punya 5+ pill buttons (Production, MCP, History,
 * Ask AI, Theme, Login) — terlalu padat secara visual. Refactor: semua
 * secondary action (MCP, History, Theme, Sign out) collapse ke avatar
 * dropdown via UserMenu. TopBar tinggal essentials:
 *
 *   [hamburger]  [logo]  [search pill, flex-1]  [avatar/login]
 *
 * Power user tetap punya akses via keyboard:
 *   - ⌘K → command palette (search + jump)
 *   - ⌘J → Ask AI focus (textarea di right panel atau bottom bar)
 *
 * Pattern reference: GitHub / Linear / Notion — secondary nav di avatar.
 */
export function TopBar() {
  const panels = usePanels();
  return (
    <header className="h-16 shrink-0 border-b-[3px] border-border-strong bg-base z-40 flex items-center px-4 gap-3">
      {/* Mobile hamburger */}
      <button
        type="button"
        onClick={() => setSidebarOpen(!panels.sidebarOpen)}
        className="lg:hidden w-10 h-10 rounded-pill border-2 border-border-strong bg-elevated shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none transition-all flex items-center justify-center"
        aria-label="Toggle sidebar"
      >
        <Menu size={16} />
      </button>

      {/* Logo */}
      <Link to="/" className="flex items-center gap-2 shrink-0">
        <div className="w-9 h-9 rounded-pill border-2 border-border-strong bg-accent shadow-brutal-sm flex items-center justify-center">
          <span className="text-base font-display font-extrabold text-fg">B</span>
        </div>
        <span className="font-display font-extrabold tracking-tight hidden sm:inline text-lg">
          Playground
        </span>
      </Link>

      {/* Search trigger — opens cmd palette (⌘K).
          Mobile: icon-only square. Desktop: full pill + label + ⌘K hint. */}
      <button
        type="button"
        onClick={() => {
          window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
        }}
        className="md:flex-1 max-w-md flex items-center gap-2 px-3 md:px-4 h-10 rounded-pill border-2 border-border-strong bg-elevated shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none transition-all text-fg-secondary text-sm"
        aria-label="Search endpoints"
      >
        <Search size={14} />
        <span className="hidden md:inline flex-1 text-left">Search endpoints...</span>
        <kbd className="hidden md:inline px-1.5 py-0.5 text-2xs font-mono bg-base border-2 border-border-strong rounded">⌘K</kbd>
      </button>

      <div className="flex-1" />

      {/* User menu — avatar dropdown carries semua secondary actions
          (MCP setup, History, Theme toggle, Sign out). Untuk logged-out,
          renders LoginButton ghost variant. */}
      <UserMenu />
    </header>
  );
}
